/**
 * Client-Side Consent Utilities
 *
 * Browser-specific consent utilities that require DOM access.
 * Use these for client-side consent checking and management.
 *
 * For server-side/pure functions, use the main exports from "@ascent/consent".
 *
 * Architecture:
 * 1. OneTrust banner loads and user makes choice
 * 2. OptanonWrapper callback detects consent state from OneTrust cookie
 * 3. Page reloads if consent changed
 * 4. Client code checks OneTrust cookie before initializing tracking
 */

import { ONETRUST_CONSENT_COOKIE } from "./constants";
import { ConsentState, type ConsentAwareOptions } from "./types";

// =============================================================================
// Re-export types and constants for convenience
// =============================================================================

export {
  CONSENT_VALUE_NO,
  CONSENT_VALUE_YES,
  ONETRUST_ALERTBOX_COOKIE,
  ONETRUST_CONSENT_COOKIE,
} from "./constants";
export { ConsentState } from "./types";
export type {
  ConsentAwareOptions,
  ConsentMode,
  ModuleConsentOptions,
} from "./types";

// =============================================================================
// Cookie Reading
// =============================================================================

/**
 * Reads a cookie value by name from document.cookie.
 *
 * @param name - Cookie name to read
 * @returns Cookie value or undefined if not found
 *
 * @example
 * ```typescript
 * const consent = readCookie("gdpr_opt_in"); // "1", "0", or undefined
 * ```
 */
export function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);

  if (parts.length > 1) {
    return parts.pop()?.split(";").shift();
  }

  return undefined;
}

/**
 * Writes a cookie to document.cookie.
 *
 * @param name - Cookie name
 * @param value - Cookie value
 * @param maxAgeSeconds - Max age in seconds
 * @param path - Cookie path (default: "/")
 */
export function writeCookie(
  name: string,
  value: string,
  maxAgeSeconds: number,
  path = "/",
): void {
  if (typeof document === "undefined") {
    return;
  }

  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:";
  const parts = [`${name}=${value}`];

  parts.push(`path=${path}`);
  parts.push(`max-age=${maxAgeSeconds}`);

  const expires = new Date(Date.now() + maxAgeSeconds * 1000).toUTCString();
  parts.push(`expires=${expires}`);

  if (secure) {
    parts.push("SameSite=None");
    parts.push("secure");
  } else {
    parts.push("SameSite=Lax");
  }

  document.cookie = parts.join(";");
}

// =============================================================================
// OneTrust Cookie Parsing
// =============================================================================

/**
 * Parses a query-string-like value (e.g., "key1=value1&key2=value2")
 * @internal
 */
function getParam(str: string, key: string): string {
  const parts = str.split("&");
  for (const part of parts) {
    const [k, v] = part.split("=");
    if (k === key) {
      try {
        return decodeURIComponent(v || "");
      } catch {
        return "";
      }
    }
  }
  return "";
}

/**
 * Parses the OneTrust consent cookie and returns consent information.
 *
 * @returns Object with consent groups and whether all are consented
 *
 * @example
 * ```typescript
 * const { hasConsentedAll, groups } = parseOneTrustCookie();
 * if (hasConsentedAll) {
 *   initializeTracking();
 * }
 * ```
 */
export function parseOneTrustCookie(): {
  hasConsentedAll: boolean;
  groups: Record<string, string>;
  isReady: boolean;
} {
  const cookieValue = readCookie(ONETRUST_CONSENT_COOKIE) || "";
  const groupsStr = getParam(cookieValue, "groups");

  if (!groupsStr) {
    return {
      hasConsentedAll: false,
      groups: {},
      isReady: false,
    };
  }

  // Parse groups (e.g., "C0001:1,C0002:1,C0003:0,C0004:1")
  const groups: Record<string, string> = {};
  let hasConsentedAll = true;
  const groupPairs = groupsStr.split(",");

  for (const pair of groupPairs) {
    const [category, consent] = pair.split(":");
    groups[category] = consent || "0";
    if (parseInt(consent || "0", 10) === 0) {
      hasConsentedAll = false;
    }
  }

  return {
    hasConsentedAll,
    groups,
    isReady: true,
  };
}

// =============================================================================
// Consent State
// =============================================================================

/**
 * Gets the current consent state by parsing the OneTrust cookie.
 *
 * @returns ConsentState enum value
 *
 * @example
 * ```typescript
 * const state = getConsentState();
 * if (state === ConsentState.CONSENTED) {
 *   initializeTracking();
 * }
 * ```
 */
export function getConsentState(): ConsentState {
  const { hasConsentedAll, isReady } = parseOneTrustCookie();

  if (!isReady) {
    return ConsentState.UNKNOWN;
  }

  return hasConsentedAll ? ConsentState.CONSENTED : ConsentState.NOT_CONSENTED;
}

/**
 * Checks if the user has consented to all tracking.
 *
 * @returns true if user has explicitly consented to all categories
 *
 * @example
 * ```typescript
 * if (hasFullConsent()) {
 *   loadGoogleAnalytics();
 * }
 * ```
 */
export function hasFullConsent(): boolean {
  return getConsentState() === ConsentState.CONSENTED;
}

/**
 * Checks if the user has made any consent decision.
 *
 * @returns true if consent state is known (either consented or not)
 *
 * @example
 * ```typescript
 * if (!hasConsentDecision()) {
 *   // Show consent banner
 * }
 * ```
 */
export function hasConsentDecision(): boolean {
  return getConsentState() !== ConsentState.UNKNOWN;
}

// =============================================================================
// Global Privacy Control (GPC)
// =============================================================================

/**
 * Checks if the Global Privacy Control (GPC) signal is active.
 * GPC is a browser setting that indicates the user wants to opt out of tracking.
 *
 * @see https://globalprivacycontrol.org/
 * @returns true if GPC signal is active
 *
 * @example
 * ```typescript
 * if (isGPCActive()) {
 *   // Respect user's privacy preference
 *   disableAllTracking();
 * }
 * ```
 */
export function isGPCActive(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }

  return (
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl === true
  );
}

// =============================================================================
// Tracking Permission Checks
// =============================================================================

/**
 * Checks if tracking should be allowed based on consent state and region.
 *
 * In opt-out regions (like CCPA), tracking is allowed by default.
 * In opt-in regions (like GDPR), tracking requires explicit consent.
 *
 * @param defaultAllowed - Whether to allow tracking when consent is unknown
 *                         (true for opt-out regions, false for opt-in regions)
 * @returns true if tracking should proceed
 *
 * @example
 * ```typescript
 * // GDPR region - require explicit consent
 * if (isTrackingAllowed(false)) {
 *   initializeTracking();
 * }
 *
 * // CCPA region - allow by default
 * if (isTrackingAllowed(true)) {
 *   initializeTracking();
 * }
 * ```
 */
export function isTrackingAllowed(defaultAllowed = true): boolean {
  const state = getConsentState();

  if (state === ConsentState.CONSENTED) {
    return true;
  }

  if (state === ConsentState.NOT_CONSENTED) {
    return false;
  }

  // Unknown state - use default (region-dependent)
  return defaultAllowed;
}

/**
 * Combined check for whether tracking should be blocked.
 * Returns true if GPC is active OR user has not consented.
 *
 * @param defaultAllowed - Default when consent is unknown
 * @returns true if tracking should be blocked
 *
 * @example
 * ```typescript
 * if (shouldBlockTracking()) {
 *   return; // Don't initialize tracking
 * }
 * ```
 */
export function shouldBlockTracking(defaultAllowed = true): boolean {
  if (isGPCActive()) {
    return true;
  }

  return !isTrackingAllowed(defaultAllowed);
}

// =============================================================================
// Consent-Aware Function Wrapper
// =============================================================================

/**
 * Wraps an initialization function to only run if consent allows.
 *
 * @param fn - Function to run if consent allows
 * @param options - Configuration options
 * @returns Wrapped function that checks consent before running
 *
 * @example
 * ```typescript
 * const safeInit = withConsentCheck(
 *   () => initializeGoogleAds(),
 *   { requireExplicitConsent: true, debug: true }
 * );
 *
 * safeInit(); // Only runs if user has consented
 * ```
 */
export function withConsentCheck<T extends (...args: any[]) => any>(
  fn: T,
  options: ConsentAwareOptions = {},
): (...args: Parameters<T>) => ReturnType<T> | undefined {
  const { requireExplicitConsent = false, debug = false } = options;

  const wrapped = (...args: Parameters<T>): ReturnType<T> | undefined => {
    // Check GPC first
    if (isGPCActive()) {
      if (debug) {
        console.log(
          `[Consent] Blocking ${fn.name || "anonymous"} - GPC active`,
        );
      }
      return undefined;
    }

    const allowed = isTrackingAllowed(!requireExplicitConsent);

    if (debug) {
      console.log(
        `[Consent] Check for ${fn.name || "anonymous"}: allowed=${allowed}, ` +
          `state=${getConsentState()}, requireExplicit=${requireExplicitConsent}`,
      );
    }

    if (!allowed) {
      if (debug) {
        console.log(
          `[Consent] Skipping ${fn.name || "anonymous"} - no consent`,
        );
      }
      return undefined;
    }

    return fn(...args) as ReturnType<T>;
  };

  return wrapped;
}
